(() => {
  'use strict';

  const CODE = 'ZYVIRA10';
  const DEFAULT_THRESHOLD = 79900;
  const CART_ENDPOINT = `${window.Shopify?.routes?.root || '/'}cart.js`;
  const SELECTED_KEY = 'zyvira-selected-discount';
  const CART_MUTATION_SELECTOR = [
    '.qty-bt',
    'delpro-btn',
    '.remove-product',
    '[name="add"]',
    '.quick-add-btn'
  ].join(',');

  let refreshTimer = 0;
  let enhanceTimer = 0;
  let requestSequence = 0;
  let observer;

  const setText = (element, value) => {
    if (element && element.textContent !== value) element.textContent = value;
  };

  const formatMoney = (cents, currency = 'INR') => {
    const amount = Math.max(0, Number(cents || 0)) / 100;
    const hasDecimals = Math.round(amount) !== amount;

    try {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency,
        minimumFractionDigits: hasDecimals ? 2 : 0,
        maximumFractionDigits: 2
      }).format(amount);
    } catch (error) {
      return `₹${amount.toFixed(hasDecimals ? 2 : 0)}`;
    }
  };

  const selectedInSession = () => {
    try {
      return sessionStorage.getItem(SELECTED_KEY) === CODE;
    } catch (error) {
      return false;
    }
  };

  const rememberSelected = () => {
    try {
      sessionStorage.setItem(SELECTED_KEY, CODE);
    } catch (error) {
      // Storage can be unavailable in strict privacy modes; checkout still works.
    }
  };

  const discountUrl = () => {
    const returnPath = `${window.location.pathname}${window.location.search}` || '/cart';
    return `/discount/${encodeURIComponent(CODE)}?redirect=${encodeURIComponent(returnPath)}`;
  };

  const offerMarkup = (context) => `
    <div class="zyvira-offer-card__eyebrow">EVERY ORDER OFFER</div>
    <h3 class="zyvira-offer-card__title">Save 10% on every order</h3>
    <p class="zyvira-offer-card__copy">Enjoy 10% off every time you shop. Apply the coupon at checkout.</p>
    <div class="zyvira-offer-card__action">
      <div class="zyvira-offer-code" aria-label="Discount code ${CODE}">
        <span>${CODE}</span>
        <small>10% off</small>
      </div>
      <button class="zyvira-apply-offer" type="button" data-context="${context}">Apply offer</button>
    </div>
    <p class="zyvira-offer-status" aria-live="polite"></p>
  `;

  const trustMarkup = `
    <div class="zyvira-cart-trust__item"><i class="ri-shield-check-line" aria-hidden="true"></i><span>Secure payments</span></div>
    <div class="zyvira-cart-trust__item"><i class="ri-truck-line" aria-hidden="true"></i><span>Dispatch in 24–48 hours</span></div>
    <div class="zyvira-cart-trust__item"><i class="ri-arrow-go-back-line" aria-hidden="true"></i><span>3-day return support</span></div>
  `;

  const markOfferSelected = (card) => {
    if (!card) return;
    card.classList.add('is-selected');
    const button = card.querySelector('.zyvira-apply-offer');
    const status = card.querySelector('.zyvira-offer-status');
    if (button) {
      button.disabled = false;
      button.textContent = `${CODE} selected`;
    }
    setText(status, 'The code is ready and will be validated at checkout.');
  };

  const ensureOfferCard = (container, context, beforeElement = null) => {
    if (!container) return null;

    let card = container.querySelector(`:scope > .zyvira-offer-card[data-zyvira-context="${context}"]`);
    if (!card) {
      card = document.createElement('section');
      card.className = 'zyvira-offer-card';
      card.dataset.zyviraContext = context;
      card.setAttribute('aria-label', `${CODE} new customer offer`);
      card.innerHTML = offerMarkup(context);

      if (beforeElement && beforeElement.parentElement === container) {
        container.insertBefore(card, beforeElement);
      } else {
        container.appendChild(card);
      }
    }

    if (selectedInSession()) markOfferSelected(card);
    return card;
  };

  const ensureTrustStrip = (container, context, afterElement = null) => {
    if (!container) return null;

    let strip = container.querySelector(`:scope > .zyvira-cart-trust[data-zyvira-context="${context}"]`);
    if (!strip) {
      strip = document.createElement('div');
      strip.className = 'zyvira-cart-trust';
      strip.dataset.zyviraContext = context;
      strip.setAttribute('aria-label', 'Zyvira purchase reassurance');
      strip.innerHTML = trustMarkup;

      if (afterElement && afterElement.parentElement === container) {
        afterElement.insertAdjacentElement('afterend', strip);
      } else {
        container.appendChild(strip);
      }
    }
    return strip;
  };

  const removeConversionUI = (scope) => {
    scope?.querySelectorAll('.zyvira-offer-card, .zyvira-cart-trust').forEach((node) => node.remove());
  };

  const enhanceCartPage = () => {
    const totalWrapper = document.querySelector('.cart-page #cart-section-total');
    const blocks = totalWrapper?.querySelector('.cart-footer .cart-blocks');
    const empty = !totalWrapper || totalWrapper.classList.contains('is-empty');

    if (empty) {
      removeConversionUI(totalWrapper);
      return;
    }

    const checkout = blocks?.querySelector('.checkout-btn');
    ensureOfferCard(blocks, 'cart-page', checkout);
    ensureTrustStrip(blocks, 'cart-page', checkout);

    if (selectedInSession()) {
      const discountInput = document.querySelector('.cart-page input[name="discount"]');
      if (discountInput && discountInput.value !== CODE) discountInput.value = CODE;
    }
  };

  const enhanceDrawer = () => {
    const drawer = document.querySelector('ajax-cart.mini-cart');
    const footer = drawer?.querySelector('.footer-mini-cart');
    const isEmpty = !drawer || drawer.classList.contains('is-empty') || drawer.querySelector('cart-pro.is-empty');

    if (isEmpty) {
      removeConversionUI(footer);
      return;
    }

    const total = footer?.querySelector('.total-price');
    const buttons = footer?.querySelector('.mini-cart-button');
    ensureOfferCard(footer, 'cart-drawer', total);
    ensureTrustStrip(footer, 'cart-drawer', buttons);

    const recommendationHeading = drawer.querySelector('.product-slider .heading h6');
    setText(recommendationHeading, 'Complete Your Look');
  };

  const normalizeShippingBar = (bar, cart) => {
    const threshold = Number.parseInt(bar.dataset.threshold || '', 10) || DEFAULT_THRESHOLD;
    const total = Number(cart.total_price || 0);
    const remaining = Math.max(0, threshold - total);
    const progress = threshold > 0 ? Math.min(100, Math.max(0, (total / threshold) * 100)) : 100;
    const unlocked = remaining <= 0;

    bar.style.setProperty('--zyvira-shipping-progress', `${progress.toFixed(2)}%`);
    bar.classList.toggle('zyvira-shipping-unlocked', unlocked);

    let text = bar.querySelector('.shipping-text');
    if (!text) {
      text = document.createElement('p');
      text.className = 'mr-0 shipping-text';
      bar.prepend(text);
    }
    text.classList.toggle('shipping-bar-success', unlocked);

    const message = unlocked
      ? 'You’ve unlocked FREE shipping'
      : `Add ${formatMoney(remaining, cart.currency || 'INR')} more for FREE shipping`;
    setText(text, message);

    if (!bar.querySelector('.ship-probar')) {
      const progressBar = document.createElement('div');
      progressBar.className = 'ship-probar';
      progressBar.setAttribute('role', 'progressbar');
      bar.appendChild(progressBar);
    }

    const progressBar = bar.querySelector('.ship-probar');
    progressBar?.setAttribute('aria-valuemin', '0');
    progressBar?.setAttribute('aria-valuemax', '100');
    progressBar?.setAttribute('aria-valuenow', String(Math.round(progress)));
    progressBar?.setAttribute('aria-label', unlocked ? 'Free shipping unlocked' : 'Progress toward free shipping');
  };

  const refreshCartState = async () => {
    const sequence = ++requestSequence;

    try {
      const response = await fetch(CART_ENDPOINT, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);

      const cart = await response.json();
      if (sequence !== requestSequence) return;

      document.querySelectorAll('.cart-page .shipping-bar, .mini-cart .shipping-bar')
        .forEach((bar) => normalizeShippingBar(bar, cart));
    } catch (error) {
      console.warn('[Zyvira cart] Unable to refresh shipping progress.', error);
    }
  };

  const enhance = () => {
    window.clearTimeout(enhanceTimer);
    enhanceTimer = window.setTimeout(() => {
      enhanceCartPage();
      enhanceDrawer();
      refreshCartState();
    }, 30);
  };

  const scheduleRefresh = () => {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(enhance, 420);
    window.setTimeout(enhance, 950);
  };

  const handleOfferClick = (event) => {
    const button = event.target.closest('.zyvira-apply-offer');
    if (!button) return;

    event.preventDefault();
    button.disabled = true;
    button.textContent = 'Applying…';

    document.querySelectorAll('input[name="discount"]').forEach((input) => {
      input.value = CODE;
    });

    rememberSelected();
    document.querySelectorAll('.zyvira-offer-card').forEach(markOfferSelected);

    window.setTimeout(() => {
      window.location.assign(discountUrl());
    }, 120);
  };

  const handlePotentialCartMutation = (event) => {
    if (event.target.closest(CART_MUTATION_SELECTOR)) scheduleRefresh();
  };

  const handleQuantityChange = (event) => {
    if (event.target.matches('.qty-box-input, [name="updates[]"]')) scheduleRefresh();
  };

  const initialize = () => {
    document.addEventListener('click', handleOfferClick);
    document.addEventListener('click', handlePotentialCartMutation, true);
    document.addEventListener('change', handleQuantityChange, true);

    observer = new MutationObserver((mutations) => {
      const relevant = mutations.some((mutation) => Array.from(mutation.addedNodes).some((node) => {
        if (!(node instanceof Element)) return false;
        return node.matches?.('ajax-cart, .cart-page, .js-contents, .footer-mini-cart, .shipping-bar') ||
          node.querySelector?.('ajax-cart, .cart-page, .js-contents, .footer-mini-cart, .shipping-bar');
      }));
      if (relevant) enhance();
    });

    observer.observe(document.body, { childList: true, subtree: true });
    enhance();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
